import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loader2, TrendingUp, Beaker, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import api from '@/services/api';

/**
 * Admin → SEO A/B Dashboard.
 *
 * Reads the aggregated docs from ``ab_summary`` (populated by
 * ``/api/analytics/track-ab-event``) and turns them into a per-experiment
 * table with impression counts, conversion counts, and computed conversion
 * rate per variant. Used to steer landing-page copy decisions.
 */
export const AdminSeoAbTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/analytics/ab-events/summary');
      setItems(r?.data?.items || []);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load A/B summary');
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Group rows by experiment, then by variant, then compute conv rate.
  const groups = useMemo(() => {
    const byExp = new Map();
    items.forEach((r) => {
      const key = r.experiment || 'unknown';
      const g = byExp.get(key) || { experiment: key, variants: new Map(), impressions: 0, conversions: 0 };
      const v = g.variants.get(r.variant) || { variant: r.variant, impressions: 0, conversions: 0, other: 0 };
      const count = Number(r.count || r.total || 0);
      if (r.event === 'impression') {
        v.impressions += count;
        g.impressions += count;
      } else if (r.event === 'cta_click' || r.event === 'conversion' || r.event === 'signup') {
        v.conversions += count;
        g.conversions += count;
      } else {
        v.other += count;
      }
      g.variants.set(r.variant, v);
      byExp.set(key, g);
    });
    return Array.from(byExp.values())
      .map((g) => ({
        ...g,
        variants: Array.from(g.variants.values()).sort((a, b) => a.variant.localeCompare(b.variant)),
      }))
      .sort((a, b) => (b.impressions - a.impressions));
  }, [items]);

  return (
    <Card className="bg-white border-slate-200 shadow-sm" data-testid="admin-seo-ab-tab">
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div className="min-w-0">
          <CardTitle className="text-slate-900 flex items-center gap-2">
            <Beaker className="w-5 h-5 text-teal-600" />
            SEO A/B Experiments
          </CardTitle>
          <CardDescription className="text-slate-500 mt-1">
            Per-variant impression + conversion counts for public landing-page experiments.
            Data comes from <code className="text-xs">/api/analytics/track-ab-event</code> and is aggregated into the
            {' '}<code className="text-xs">ab_summary</code> collection.
          </CardDescription>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="seo-ab-refresh-btn">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          <span className="ml-1">Refresh</span>
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <div className="py-12 flex items-center justify-center text-slate-500">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
          </div>
        ) : error ? (
          <div className="p-4 rounded-lg border border-red-200 bg-red-50 text-sm text-red-800" data-testid="seo-ab-error">
            {error}
          </div>
        ) : groups.length === 0 ? (
          <div className="p-6 text-center text-sm text-slate-500 border-2 border-dashed border-slate-200 rounded-lg" data-testid="seo-ab-empty">
            No experiment data yet. Once visitors hit your Industry / Location landing pages, results will appear here.
          </div>
        ) : (
          groups.map((g) => {
            const winner = [...g.variants].reduce((best, v) => {
              const rate = v.impressions ? v.conversions / v.impressions : 0;
              const bestRate = best?.impressions ? best.conversions / best.impressions : 0;
              return rate > bestRate ? v : best;
            }, null);
            return (
              <div
                key={g.experiment}
                className="border border-slate-200 rounded-lg p-4"
                data-testid={`seo-ab-experiment-${g.experiment}`}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="min-w-0">
                    <p className="font-semibold text-slate-900 truncate">{g.experiment}</p>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {g.impressions.toLocaleString()} impressions · {g.conversions.toLocaleString()} conversions
                    </p>
                  </div>
                  {winner && winner.impressions > 20 && (
                    <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">
                      <TrendingUp className="w-3 h-3 mr-1" />
                      Variant {winner.variant.toUpperCase()} leading
                    </Badge>
                  )}
                </div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b border-slate-100">
                      <th className="py-2 font-medium">Variant</th>
                      <th className="py-2 font-medium text-right">Impressions</th>
                      <th className="py-2 font-medium text-right">Conversions</th>
                      <th className="py-2 font-medium text-right">Conv. rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {g.variants.map((v) => {
                      const rate = v.impressions ? (v.conversions / v.impressions) * 100 : 0;
                      const isWinner = winner && winner.variant === v.variant && winner.impressions > 20;
                      return (
                        <tr
                          key={v.variant}
                          className="border-b border-slate-50 last:border-0"
                          data-testid={`seo-ab-row-${g.experiment}-${v.variant}`}
                        >
                          <td className="py-2 font-medium text-slate-900 uppercase">
                            {v.variant}
                            {isWinner && <span className="ml-2 text-emerald-700 text-xs">▲</span>}
                          </td>
                          <td className="py-2 text-right text-slate-700 tabular-nums">
                            {v.impressions.toLocaleString()}
                          </td>
                          <td className="py-2 text-right text-slate-700 tabular-nums">
                            {v.conversions.toLocaleString()}
                          </td>
                          <td className={`py-2 text-right tabular-nums font-medium ${isWinner ? 'text-emerald-700' : 'text-slate-700'}`}>
                            {rate.toFixed(2)}%
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            );
          })
        )}
      </CardContent>
    </Card>
  );
};

export default AdminSeoAbTab;
